import React from "react";
import {
  sanitizeErrorMessage,
  logErrorForDebugging,
} from "../../../../utility/errorSanitization";

/**
 * Formats a position number with appropriate ordinal suffix
 * @param {number|string|null|undefined} position - The position to format
 * @param {Object} options - Formatting options
 * @param {string} options.fallback - Fallback text when formatting fails (default: "N/A")
 * @param {boolean} options.includeNumber - Whether to include the number with suffix (default: true)
 * @returns {string} Formatted position string
 */
export const formatPosition = (position, options = {}) => {
  const { fallback = "N/A", includeNumber = true } = options;

  try {
    // Handle null, undefined, or non-numeric values
    if (position === null || position === undefined || position === "") {
      return fallback;
    }

    // If it's not a number, return as-is (for special cases like "Ineligible")
    if (typeof position !== "number") {
      return position;
    }

    // Handle special cases
    if (position === 0) {
      return "0th";
    }

    // Get the last digit and last two digits for suffix determination
    const lastDigit = position % 10;
    const lastTwoDigits = position % 100;

    let suffix;

    // Special cases for 11, 12, 13 (always "th")
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      suffix = "th";
    } else {
      // Regular ordinal suffix rules
      switch (lastDigit) {
        case 1:
          suffix = "st";
          break;
        case 2:
          suffix = "nd";
          break;
        case 3:
          suffix = "rd";
          break;
        default:
          suffix = "th";
          break;
      }
    }

    return includeNumber ? `${position}${suffix}` : suffix;
  } catch (err) {
    const sanitizedMessage = sanitizeErrorMessage(err, "format position");
    logErrorForDebugging(err, "format position", sanitizedMessage);
    return fallback;
  }
};

/**
 * React component for displaying position values
 * @param {Object} props - Component props
 * @param {number|string|null|undefined} props.position - The position to display
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.formatOptions - Options passed to formatPosition
 * @param {boolean} props.showLabel - Whether to show a label
 * @param {string} props.label - Label text
 * @param {string} props.fallback - Fallback text when position is invalid
 * @returns {JSX.Element} Formatted position display
 */
const PositionDisplay = ({
  position,
  className = "",
  formatOptions = {},
  showLabel = false,
  label = "Position",
  fallback = "N/A",
}) => {
  if (position === null || position === undefined || position === "") {
    return <span className={`text-gray-500 ${className}`}>{fallback}</span>;
  }

  const formattedPosition = formatPosition(position, formatOptions);

  return (
    <span className={`text-gray-700 ${className}`}>
      {showLabel && <span className="font-medium">{label}: </span>}
      {formattedPosition}
    </span>
  );
};

export default PositionDisplay;
